import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import type { ApiClient } from '../api/client';
import { useApi } from './provider';

export const reportKeys = {
  projectTime: (workspaceSlug: string, projectSlug: string, from?: string, to?: string) =>
    ['reports', 'project-time', workspaceSlug, projectSlug, from ?? '', to ?? ''] as const,
  billableSummary: (workspaceSlug: string, from?: string, to?: string) =>
    ['reports', 'billable-summary', workspaceSlug, from ?? '', to ?? ''] as const,
};

export interface ReportRangeArgs {
  /** ISO date or ISO 8601 (inclusive lower bound). */
  from?: string;
  /** ISO date or ISO 8601 (exclusive upper bound). */
  to?: string;
}

export const projectTimeRollupQueryOptions = (
  api: ApiClient,
  workspaceSlug: string,
  projectSlug: string,
  args: ReportRangeArgs = {},
) =>
  queryOptions({
    queryKey: reportKeys.projectTime(workspaceSlug, projectSlug, args.from, args.to),
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/v1/workspaces/{workspace_slug}/projects/{projectSlug}/time-entries',
        {
          params: {
            path: { workspace_slug: workspaceSlug, projectSlug },
            query: stripUndefined(args),
          },
        },
      );
      if (error) throw error;
      return data;
    },
  });

export const useProjectTimeRollup = (
  workspaceSlug: string | undefined,
  projectSlug: string | undefined,
  args: ReportRangeArgs = {},
) => {
  const api = useApi();
  return useQuery({
    ...projectTimeRollupQueryOptions(api, workspaceSlug ?? '', projectSlug ?? '', args),
    enabled: Boolean(workspaceSlug && projectSlug),
  });
};

export const billableSummaryQueryOptions = (
  api: ApiClient,
  workspaceSlug: string,
  args: ReportRangeArgs = {},
) =>
  queryOptions({
    queryKey: reportKeys.billableSummary(workspaceSlug, args.from, args.to),
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/v1/workspaces/{workspace_slug}/reports/billable-summary',
        {
          params: {
            path: { workspace_slug: workspaceSlug },
            query: stripUndefined(args),
          },
        },
      );
      if (error) throw error;
      return data;
    },
  });

export const useBillableSummary = (
  workspaceSlug: string | undefined,
  args: ReportRangeArgs = {},
) => {
  const api = useApi();
  return useQuery({
    ...billableSummaryQueryOptions(api, workspaceSlug ?? '', args),
    enabled: Boolean(workspaceSlug),
  });
};

interface SignedCsvUrlResponse {
  data?: { url?: string; expires_at?: string };
}

/**
 * Two-step CSV download:
 *   1. POST to the `/sign` endpoint while authenticated, get a signed URL
 *   2. Trigger a browser download by setting `window.location` to the URL
 *
 * The signed URL self-authenticates — no bearer header on the GET, which
 * is exactly what `<a href download>` would have wanted but couldn't do
 * because of the auth requirement on the original route.
 *
 * `from` / `to` are baked into the signature; tampering with the URL
 * produces 403 with `api.errors.invalid_signed_url`.
 */
export const useDownloadProjectTimeEntriesCsv = (
  workspaceSlug: string,
  projectSlug: string,
) => {
  const api = useApi();
  return useMutation({
    mutationFn: async (args: ReportRangeArgs = {}) => {
      const { data, error } = await api.POST(
        '/v1/workspaces/{workspace_slug}/projects/{projectSlug}/time-entries.csv/sign',
        {
          params: {
            path: { workspace_slug: workspaceSlug, projectSlug },
            query: stripUndefined(args),
          },
        },
      );
      if (error) throw error;
      const url = (data as SignedCsvUrlResponse | undefined)?.data?.url;
      if (!url) throw new Error('Server did not return a signed URL.');
      triggerBrowserDownload(url);
      return data;
    },
  });
};

export const useDownloadBillableSummaryCsv = (workspaceSlug: string) => {
  const api = useApi();
  return useMutation({
    mutationFn: async (args: ReportRangeArgs = {}) => {
      const { data, error } = await api.POST(
        '/v1/workspaces/{workspace_slug}/reports/billable-summary.csv/sign',
        {
          params: {
            path: { workspace_slug: workspaceSlug },
            query: stripUndefined(args),
          },
        },
      );
      if (error) throw error;
      const url = (data as SignedCsvUrlResponse | undefined)?.data?.url;
      if (!url) throw new Error('Server did not return a signed URL.');
      triggerBrowserDownload(url);
      return data;
    },
  });
};

const stripUndefined = <T extends object>(obj: T): T => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== '') {
      out[k] = v;
    }
  }
  return out as T;
};

/**
 * Web-only download trigger. Mobile / desktop will plug their own platform
 * primitive in (per `app/PRODUCT.md` §7's platform seams) — for now this
 * lives in shared because every consumer today is web.
 */
const triggerBrowserDownload = (url: string): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('Browser download trigger called outside a DOM environment.');
  }
  // Anchor click avoids navigating away from the SPA. The browser sees
  // Content-Disposition: attachment from the server and saves the file.
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
};
